<template>
  <div>
    <!-- Enhanced Filters Section -->
    <b-card class="mb-3">
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            class="mr-1"
          />
          Filtres
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row class="mt-1">
          <b-col
            v-if="['admin', 'superadmin', 'customer_support'].includes(user.role)"
            md="3"
          >
            <b-form-group
              label="Dépôt"
              label-for="repository"
            >
              <v-select
                v-model="filters.repository"
                :clearable="true"
                placeholder="Sélectionner un dépôt"
                label="name"
                :options="repositories"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Statut cible"
              label-for="targetStatus"
            >
              <b-form-select
                v-model="filters.targetStatus"
                :options="statusOptions"
                @change="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Vérification Manager"
              label-for="verification"
            >
              <b-form-select
                v-model="filters.isVerified"
                :options="verifiedOptions"
                @change="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date de création"
              label-for="date"
            >
              <b-form-datepicker
                v-model="filters.date"
                v-bind="datePickerConfig"
                placeholder="Sélectionner une date"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Reset Filters -->
        <b-row class="mt-2 text-right">
          <b-col>
            <b-button
              variant="outline-secondary"
              @click="clearFilters"
            >
              Effacer les filtres
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Action Buttons -->
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-end">
        <b-button
          :to="{ name: user.role === 'manager' ? 'bon-retour-definitive-manager-scan' : 'bon-retour-definitive-add' }"
          variant="gradient-danger"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Nouveau retour définitif</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Data Table -->
    <b-card no-body>
      <vue-good-table
        :columns="columns"
        :rows="vouchers"
        :search-options="{ enabled: true }"
        :pagination-options="{ enabled: true, perPage: 10 }"
        :is-loading="isLoading"
        style-class="vgt-table striped"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'id'">
            <strong>#{{ props.row.id }}</strong>
          </span>

          <span v-else-if="props.column.field === 'target_status'">
            <b-badge :variant="props.row.target_status === 22 ? 'light-danger' : 'light-warning'">
              {{ props.row.target_status === 22 ? 'Retour définitif' : 'Relance' }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'created_at'">
            {{ formatFn(props.row.created_at) }}
          </span>

          <span v-else-if="props.column.field === 'is_verified'">
            <div v-if="props.row.is_verified">
              <b-badge
                variant="light-success"
                class="mb-50"
              >Vérifié</b-badge>
              <div
                v-if="props.row.verified_by_username"
                class="small text-muted"
              >
                Par: {{ props.row.verified_by_username }}
              </div>
              <div
                v-if="props.row.verified_at"
                class="small text-muted"
              >
                Le: {{ formatFn(props.row.verified_at) }}
              </div>
            </div>
            <b-badge
              v-else
              variant="light-secondary"
            >En attente</b-badge>
          </span>

          <span v-else-if="props.column.field === 'action'">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'bon-retour-definitive-show', params: { id: props.row.id } }"
            >
              <feather-icon
                icon="EyeIcon"
                class="mr-50"
              />
              Détail
            </b-button>
          </span>
        </template>
      </vue-good-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormSelect, BFormDatepicker, BButton, BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormDatepicker,
    BButton,
    BBadge,
    VueGoodTable,
    vSelect,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      vouchers: [],
      repositories: [],
      filters: {
        repository: null,
        targetStatus: '',
        isVerified: '',
        date: '',
      },
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 22, text: 'Retour définitif' },
        { value: 23, text: 'Relance' },
      ],
      verifiedOptions: [
        { value: '', text: 'Tous' },
        { value: 'true', text: 'Vérifié' },
        { value: 'false', text: 'En attente' },
      ],
      columns: [
        { label: 'Référence', field: 'id' },
        { label: 'Dépôt', field: 'repository.name' },
        { label: 'Statut cible', field: 'target_status' },
        { label: 'Colis', field: 'orders_count' },
        { label: 'Vérification', field: 'is_verified' },
        { label: 'Date', field: 'created_at' },
        { label: 'Action', field: 'action', sortable: false },
      ],
      datePickerConfig: {
        locale: 'fr-FR',
      },
    }
  },
  created() {
    this.user = storeAuth.state.user
    this.fetchVouchers()
    if (['admin', 'superadmin', 'customer_support'].includes(this.user.role)) {
      this.fetchRepositories()
    }
  },
  methods: {
    async fetchVouchers() {
      this.isLoading = true
      try {
        const params = {}
        if (this.filters.repository) params.repository = this.filters.repository.id
        if (this.filters.targetStatus) params.target_status = this.filters.targetStatus
        if (this.filters.isVerified !== '') params.is_verified = this.filters.isVerified
        if (this.filters.date) params.created_at__date = this.filters.date

        const { data } = await axios.get('/api/return-vouchers/definitive/', { params })
        this.vouchers = data.results || data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching definitive return vouchers', error)
        const message = error.response?.data?.detail || error.response?.data?.error || 'Erreur lors du chargement des traitements anomalie'
        this.showToast('danger', message)
      } finally {
        this.isLoading = false
      }
    },
    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data.results || data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories', error)
      }
    },
    applyFilters() {
      this.fetchVouchers()
    },
    clearFilters() {
      this.filters = {
        repository: null,
        targetStatus: '',
        isVerified: '',
        date: '',
      }
      this.fetchVouchers()
    },
    formatFn(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleString('fr-FR')
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Traitement anomalie',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
